import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { debounce } from 'lodash';

import { reportAxiosError } from '~/Utils';

// Generic interfaces for autocomplete functionality
export interface AutocompleteOption {
  id: string | number;
  name: string;
}

export interface AutocompleteResponse<T extends AutocompleteOption> {
  suggestions: T[];
  total_count?: number;
}

export interface AutocompleteParams {
  search_term: string;
  limit: number;
  [key: string]: unknown; // Allow additional filters
}

export interface UseAutocompleteProps {
  organizationId: number;
  fetchUrl: string;
  debounceMs?: number;
  limit?: number;
  additionalParams?: Record<string, unknown>;
}

export interface UseAutocompleteReturn<T extends AutocompleteOption> {
  suggestions: T[];
  isLoading: boolean;
  fetchSuggestions: (searchTerm: string) => void;
}

const useAutocomplete = <T extends AutocompleteOption>({
  fetchUrl,
  debounceMs = 300,
  limit = 10,
  additionalParams = {},
}: UseAutocompleteProps): UseAutocompleteReturn<T> => {
  const [suggestions, setSuggestions] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Memoize additionalParams to prevent infinite loops
  const memoizedAdditionalParams = useMemo(
    () => additionalParams,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(additionalParams)]
  );

  // Create a stable reference to the debounced function
  const debouncedFetch = useMemo(
    () =>
      debounce(async (searchTerm: string) => {
        try {
          setIsLoading(true);
          const params: AutocompleteParams = {
            search_term: searchTerm,
            limit,
            ...memoizedAdditionalParams,
          };

          const response = await axios.get<AutocompleteResponse<T>>(fetchUrl, { params });
          setSuggestions(response.data.suggestions || []);
        } catch (error) {
          await reportAxiosError(error);
          setSuggestions([]);
        } finally {
          setIsLoading(false);
        }
      }, debounceMs),
    [fetchUrl, debounceMs, limit, memoizedAdditionalParams]
  );

  // Create a stable wrapper function
  const fetchSuggestions = useCallback(
    (searchTerm: string) => {
      debouncedFetch(searchTerm);
    },
    [debouncedFetch]
  );

  // Load initial options only once when the hook mounts
  useEffect(() => {
    let mounted = true;

    const loadInitialOptions = async () => {
      try {
        setIsLoading(true);
        const params: AutocompleteParams = {
          search_term: '',
          limit,
          ...memoizedAdditionalParams,
        };

        const response = await axios.get<AutocompleteResponse<T>>(fetchUrl, { params });
        if (mounted) {
          setSuggestions(response.data.suggestions || []);
        }
      } catch (error) {
        if (mounted) {
          await reportAxiosError(error);
          setSuggestions([]);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadInitialOptions();

    return () => {
      mounted = false;
    };
    // Only depend on stable values that won't cause re-runs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUrl, limit]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  return {
    suggestions,
    isLoading,
    fetchSuggestions,
  };
};

export default useAutocomplete;
